import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {useLocation, useNavigate} from 'react-router-dom';
import {LogIn, LogOut, Wrench} from 'lucide-react';
import {AuthMenuQuery} from './__generated__/AuthMenuQuery.graphql';

const QUERY = graphql`
    query AuthMenuQuery {
        viewer {
            id
            name
            image
            admin
        }
        facebookLoginEnabled
    }`;

export const csrfToken = (): string =>
    document.querySelector("meta[name='csrf-token']")?.getAttribute('content') ?? '';

export const logout = async () => {
    await fetch('/logout', {
        method: 'DELETE',
        headers: {'X-CSRF-Token': csrfToken(), Accept: 'application/json'},
    });
    window.location.href = '/';
};

// OmniAuth only accepts POST for the request phase, so the login button is a form, not a link.
export const FacebookLoginForm: React.FC<{ className?: string; children: React.ReactNode }> = ({className, children}) => {
    const location = useLocation();
    return (
        <form method="post" action="/auth/facebook" className="inline">
            <input type="hidden" name="authenticity_token" value={csrfToken()}/>
            <input type="hidden" name="origin" value={location.pathname}/>
            <button type="submit" className={className}>{children}</button>
        </form>
    );
};

// Customers (and visitors) get the portal; staff see Ops instead.
const ClientPortalButton: React.FC = () => {
    const navigate = useNavigate();
    return (
        <button onClick={() => navigate('/client')}
                className="inline-flex items-center gap-2 h-9 px-4 whitespace-nowrap text-xs font-bold uppercase tracking-wider text-lumina-accent border border-lumina-accent rounded hover:bg-lumina-accent hover:text-black transition-all duration-300">
            CLIENT PORTAL
        </button>
    );
};

const AuthMenuContent: React.FC = () => {
    const data = useLazyLoadQuery<AuthMenuQuery>(QUERY, {}, {fetchPolicy: 'store-or-network'});
    const navigate = useNavigate();

    if (!data.viewer) {
        return (
            <div className="flex items-center gap-2">
                <ClientPortalButton/>
                {data.facebookLoginEnabled && (
                    <FacebookLoginForm
                        className="inline-flex items-center gap-2 h-9 px-4 whitespace-nowrap text-xs font-bold uppercase tracking-wider text-gray-300 border border-gray-700 rounded hover:border-lumina-accent hover:text-white transition-colors">
                        <LogIn className="w-3 h-3"/>
                        SIGN IN
                    </FacebookLoginForm>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            {data.viewer.admin ? (
                <button onClick={() => navigate('/ops')}
                        className="inline-flex items-center gap-2 h-9 px-4 whitespace-nowrap text-xs font-bold uppercase tracking-wider text-lumina-purple border border-lumina-purple/40 rounded hover:bg-lumina-purple/10 transition-colors">
                    <Wrench className="w-3 h-3"/>
                    OPS
                </button>
            ) : (
                <ClientPortalButton/>
            )}
            {data.viewer.image && (
                <img src={data.viewer.image} alt={data.viewer.name ?? ''}
                     className="w-8 h-8 rounded-full border border-gray-700"/>
            )}
            <button onClick={logout} title={`Sign out ${data.viewer.name ?? ''}`}
                    className="text-gray-400 hover:text-white transition-colors">
                <LogOut className="w-4 h-4"/>
            </button>
        </div>
    );
};

// Renders nothing while the viewer query loads so the navbar never suspends the whole page.
const AuthMenu: React.FC = () => (
    <Suspense fallback={null}>
        <AuthMenuContent/>
    </Suspense>
);

export default AuthMenu;
